/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatters } from "@/utils/timeSettings";

interface MiniAreaChartProps {
  data: { time: string; value: number }[];
  color: string;
  gradientId: string;
  height?: number;
  valueFormatter: (value: number) => string;
  emptyLabel: string;
}

export const MiniAreaChart: React.FC<MiniAreaChartProps> = ({
  data,
  color,
  gradientId,
  height = 120,
  valueFormatter,
  emptyLabel,
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-gray-400 dark:text-gray-500"
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.2} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} />
        <XAxis
          dataKey="time"
          tickFormatter={(t) => formatters.time(new Date(t))}
          tick={{ fontSize: 10 }}
          minTickGap={40}
        />
        <YAxis tickFormatter={valueFormatter} tick={{ fontSize: 10 }} width={48} />
        <Tooltip
          formatter={(value: number) => valueFormatter(value)}
          labelFormatter={(label) => formatters.time(new Date(label))}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
